/** @jsx jsx */
import React, { type PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { jsx } from '@compiled/react';
import type { CommentProps } from '@atlaskit/comment';
import { useIntl } from '@atlassian/jira-intl';
import type { Permalink } from '@atlassian/jira-issue-view-common/src/component/permalink-button/permalink-provider/types.tsx';
import { createSampleAdf } from '@atlassian/jira-issue-view-common-utils/src/test/index.tsx';
import {
	COMMENT_VISIBILITY_PUBLIC,
	type THIRD_PARTY_SOURCE,
} from '@atlassian/jira-issue-view-common-types/src/comment-type.tsx';
import type { ADF } from '@atlassian/jira-rich-content/src/index.tsx';
import messages from '../messages.tsx';
import type { ComponentProps } from '../types.tsx';

type ComponentBaseProps = Omit<ComponentProps, 'children' | 'intl'>;
type AkCommentProps = Pick<CommentProps, 'actions' | 'time' | 'content' | 'errorActions'>;
type Props = AkCommentProps &
	ComponentBaseProps & {
		authorDisplayName?: string;
		authorAdditionalInfo?: React.ReactNode;
		shouldHighlightComment?: boolean;
		hasChildren: boolean;
		isUnreadComment?: boolean;
		showNewUnreadCommentLozenge: boolean;
		restrictedToText: string;
		permalink: Permalink;
		displayNameForUpdateAuthor?: string;
		floatingMenu?: React.ReactNode;
		isPinned?: boolean;

		setUnreadCommentScrollRef?: (ref: HTMLElement) => void;
		selectedEventOccurredAt?: number | null;
		setSelectedEventOccuredAt?: (val: number | null) => void;
		setShowNewUnreadCommentLozenge?: (val: boolean) => void;
		optimisticCommentContainer?: HTMLElement | null | undefined;
		setOptimisticCommentContainer?: (container: HTMLElement | null) => void;
		commentPermalinkContainer?: HTMLElement | null | undefined;
		setCommentPermalinkContainer?: (container: HTMLElement | null) => void;
		unreadCommentElement?: HTMLElement | null | undefined;
		isThirdPartySyncOn?: boolean;
		thirdPartyLink?: string;
		thirdPartyCommentSource?: THIRD_PARTY_SOURCE;
		onPinClick?: () => void;
		onUnpinClick?: () => void;
		canPin?: boolean;
		isPinnedCommentsLimitReached?: boolean;
		pinnedCommentBodyAdf?: ADF;
		canScrollToOriginalCommentFromPinned?: boolean;
	};

type Context = Props & {
	isSaving: boolean;
	isError: boolean;
	savingText: string;
	shouldHighlightComment: boolean;
};

const CommentContext = createContext<Context>({
	isThreadedCommentsEnabled: false,
	updatedDate: null,
	commentId: '',
	isOptimistic: false,
	jsdIncidentActivityViewHidden: null,
	eventOccurredAt: null,
	isInternal: false,
	isEditing: false,
	hasSaveFailed: false,
	hasChildren: false,
	createdDate: '',
	fullIssueUrl: '',
	onCopyClick: () => undefined,
	permalink: {
		isVisible: false,
		show: () => undefined,
		hide: () => undefined,
	},
	showNewUnreadCommentLozenge: false,
	showLozenge: undefined,
	showAddedCommentDescription: undefined,
	hasDraft: false,
	edited: false,
	parentId: undefined,
	isSaving: false,
	isError: false,
	savingText: 'Saving...',
	isIssueOfIncidentsPractice: false,
	isHighlighted: false,
	hasScrolledPermalink: false,
	shouldScrollAfterAsyncComponentsLoaded: false,
	shouldShowCommentVisibility: false,
	canEdit: false,
	canDelete: false,
	bodyAdf: createSampleAdf(''),
	bodyHtml: '',
	createAnalyticsEvent: undefined,
	showReactions: false,
	visibility: COMMENT_VISIBILITY_PUBLIC,
	visibilityEdited: {
		type: 'role',
		value: '',
		groupId: undefined,
	},
	isEditingInternal: false,
	setScrollStatus: () => undefined,
	getOnEditSave: () => undefined,
	getOnEditClick: () => undefined,
	onSaveRetry: () => undefined,
	onSaveEdit: () => undefined,
	onSaveCancel: () => undefined,
	onEditCancel: () => undefined,
	onEditUpdate: () => undefined,
	onEditPaste: () => undefined,
	onBlur: () => undefined,
	onCommentVisibilityChange: () => undefined,
	topMostUnreadCommentId: null,
	authorDisplayName: '',
	authorAdditionalInfo: undefined,
	isUnreadComment: false,
	displayNameForUpdateAuthor: '',
	restrictedToText: '',
	shouldHighlightComment: false,
	selectedEventOccurredAt: null,
	setSelectedEventOccuredAt: () => undefined,
	setShowNewUnreadCommentLozenge: () => undefined,
	setOptimisticCommentContainer: () => undefined,
	setCommentPermalinkContainer: () => undefined,
	commentPermalinkContainer: undefined,
	unreadCommentElement: undefined,
	isPinned: false,
	onPinClick: () => undefined,
	onUnpinClick: () => undefined,
	canPin: false,
	isPinnedCommentsLimitReached: false,
	pinnedCommentBodyAdf: createSampleAdf(''),
	isThirdPartySyncOn: undefined,
	thirdPartyLink: undefined,
	canScrollToOriginalCommentFromPinned: false,
});

export const useCommentContext = (): Context => {
	const context = useContext(CommentContext);
	if (!context) {
		throw new Error('useCommentContext must be used within a CommentContextProvider');
	}
	return context;
};

const CommentContextProvider = ({ children, ...restProps }: PropsWithChildren<Props>) => {
	const {
		hasSaveFailed,
		isEditing,
		isOptimistic,
		isDeleted,
		actions,
		isInternal,
		isHighlighted,
		shouldHighlightComment,
	} = restProps;
	const { formatMessage } = useIntl();
	const value = useMemo(
		() => ({
			...restProps,
			isError: hasSaveFailed && !isEditing,
			isSaving: isOptimistic && !hasSaveFailed,
			savingText: formatMessage(messages.saving),
			actions: !isDeleted && !isOptimistic && !isEditing ? actions : [],
			shouldHighlightComment:
				shouldHighlightComment || (!isInternal && !isEditing && isHighlighted),
		}),
		[
			actions,
			formatMessage,
			hasSaveFailed,
			isDeleted,
			isEditing,
			isHighlighted,
			isInternal,
			isOptimistic,
			restProps,
			shouldHighlightComment,
		],
	);

	return <CommentContext.Provider value={value}>{children}</CommentContext.Provider>;
};

export default CommentContextProvider;
